import { useState, CSSProperties } from "react";
import { useNavigate } from "react-router-dom";
import { openBillingPortalCall } from "../../../api/apiOperations";
import { cancelCurrentMembership, pauseMembershipApi } from "../../../api/apiEndpoints";
import banner from "../../../assets/images/PaymentFailedBanner.jpg";

const PAPER = "#faf8f4";
const INK = "#594d46";
const LINE = "#ececea";
const TILE = "#f3ede3";

type Props = { open: boolean; onResolved: () => void };

export default function FailedPaymentSheet({ open, onResolved }: Props) {
  const navigate = useNavigate();
  const [view, setView] = useState<"main" | "pauseCancel">("main");
  const [busy, setBusy] = useState(false);

  if (!open) return null;

  const run = async (fn: () => Promise<void>) => {
    if (busy) return;
    setBusy(true);
    try { await fn(); } finally { setBusy(false); }
  };

  const updateCard = () => run(async () => {
    const url = await openBillingPortalCall();
    if (url) window.location.href = url;
    else alert("Could not open the payment page. Please try again.");
  });

  // Navigate to intro selection — the member stays past-due (gate re-appears if they bail).
  // The delinquent sub is voided/canceled at checkout when they actually pick a new intro.
  const hop = () => navigate("/select-gym");

  const pause = () => run(async () => {
    try { await pauseMembershipApi(30); onResolved(); }
    catch { alert("Could not pause your membership. Please try again."); }
  });

  const cancel = () => run(async () => {
    try { await cancelCurrentMembership(); onResolved(); }
    catch { alert("Could not cancel your membership. Please try again."); }
  });

  return (
    <div style={scrim}>
      <div style={sheet}>
        <div style={grab} />

        {view === "main" ? (
          <>
            <div style={bannerWrap}>
              <img src={banner} alt="" style={bannerImg} />
              <div style={bannerFade} />
            </div>

            <div style={mainBody}>
              <h2 style={title}>Your payment didn't go through.</h2>
              <p style={lede}>Your payment failed, but no worries. What would you like to do next?</p>

              <button style={{ ...opt, ...optPrimary }} onClick={updateCard} disabled={busy}>
                <span style={{ ...tile, ...tilePrimary }}><CardIcon /></span>
                <span style={optText}><span style={{ ...optTitle, ...optTitlePrimary }}>Update payment method</span></span>
                <Chev light />
              </button>
              <button style={opt} onClick={hop} disabled={busy}>
                <span style={tile}><DumbbellIcon /></span>
                <span style={optText}><span style={optTitle}>Hop to a new intro offer</span></span>
                <Chev />
              </button>
              <button style={opt} onClick={() => setView("pauseCancel")} disabled={busy}>
                <span style={tile}><PauseIcon /></span>
                <span style={optText}><span style={optTitle}>Pause or cancel</span></span>
                <Chev />
              </button>
            </div>
          </>
        ) : (
          <div style={subCenter}>
            <div style={doneico}><PauseIcon big /></div>
            <h2 style={{ ...title, textAlign: "center" }}>Take the time you need.</h2>
            <p style={{ ...lede, textAlign: "center" }}>We'll stop collecting payment and hold your spot. Come back whenever you're ready.</p>

            <button style={btn} onClick={pause} disabled={busy}>Pause my membership</button>
            <button style={{ ...btn, ...btnGhost }} onClick={cancel} disabled={busy}>Cancel instead</button>
            <button style={textBtn} onClick={() => setView("main")} disabled={busy}>Back</button>
          </div>
        )}
      </div>
    </div>
  );
}

const Chev = ({ light }: { light?: boolean }) => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" style={{ flex: "0 0 auto" }}>
    <path d="M7 4l5 5-5 5" stroke={light ? "#fff" : INK} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const CardIcon = () => (
  <svg width="19" height="19" viewBox="0 0 24 24" fill="none">
    <rect x="3" y="6" width="18" height="13" rx="2.5" stroke="#fff" strokeWidth="1.7" />
    <path d="M3 10h18" stroke="#fff" strokeWidth="1.7" />
  </svg>
);
const DumbbellIcon = () => (
  <svg width="19" height="19" viewBox="0 0 24 24" fill="none">
    <path d="M4 9v6M6.5 7.5v9M17.5 7.5v9M20 9v6M6.5 12h11" stroke={INK} strokeWidth="1.7" strokeLinecap="round" />
  </svg>
);
const PauseIcon = ({ big }: { big?: boolean }) => (
  <svg width={big ? 24 : 19} height={big ? 24 : 19} viewBox="0 0 24 24" fill="none">
    <rect x="7" y="6" width="3.2" height="12" rx="1.2" stroke={INK} strokeWidth="1.6" />
    <rect x="13.8" y="6" width="3.2" height="12" rx="1.2" stroke={INK} strokeWidth="1.6" />
  </svg>
);

const scrim: CSSProperties = {
  position: "fixed", inset: 0, background: "rgba(20,16,12,0.5)", zIndex: 9999,
  display: "flex", alignItems: "flex-end", justifyContent: "center",
};
const sheet: CSSProperties = {
  display: "flex", flexDirection: "column",
  background: PAPER, borderTopLeftRadius: 28, borderTopRightRadius: 28,
  width: "100%", maxWidth: 440, padding: 26, paddingBottom: 34, boxSizing: "border-box",
  minHeight: "72vh", maxHeight: "96vh", boxShadow: "0 -8px 40px rgba(0,0,0,0.18)",
};
const grab: CSSProperties = { flex: "0 0 auto", width: 38, height: 4, borderRadius: 3, background: "#d8d0c4", margin: "0 auto 16px" };
const bannerWrap: CSSProperties = {
  position: "relative", margin: "0 -26px 10px", height: 185,
  borderTopLeftRadius: 18, borderTopRightRadius: 18, overflow: "hidden", background: TILE, flex: "0 0 auto",
};
const bannerImg: CSSProperties = { width: "100%", height: "100%", objectFit: "cover", display: "block" };
const bannerFade: CSSProperties = { position: "absolute", inset: 0, background: `linear-gradient(180deg, rgba(250,248,244,0) 60%, ${PAPER} 100%)` };
const mainBody: CSSProperties = { flex: 1, display: "flex", flexDirection: "column", justifyContent: "center" };
const subCenter: CSSProperties = { flex: 1, display: "flex", flexDirection: "column", justifyContent: "center" };
const title: CSSProperties = { fontSize: 23, fontWeight: 700, color: "#000", letterSpacing: "-0.4px", margin: 0, lineHeight: 1.2 };
const lede: CSSProperties = { fontSize: 15, color: INK, lineHeight: 1.5, margin: "10px 0 22px" };
const opt: CSSProperties = {
  display: "flex", alignItems: "center", gap: 14, width: "100%", textAlign: "left",
  background: "#fff", border: `1px solid ${LINE}`, borderRadius: 16, padding: "15px 16px", marginBottom: 11, cursor: "pointer",
};
const optPrimary: CSSProperties = { background: "#000", borderColor: "#000" };
const tile: CSSProperties = { flex: "0 0 auto", width: 38, height: 38, borderRadius: 11, background: TILE, display: "flex", alignItems: "center", justifyContent: "center" };
const tilePrimary: CSSProperties = { background: "rgba(255,255,255,0.14)" };
const optText: CSSProperties = { flex: 1 };
const optTitle: CSSProperties = { fontSize: 15.5, fontWeight: 600, color: "#000", letterSpacing: "-0.2px" };
const optTitlePrimary: CSSProperties = { color: "#fff" };
const doneico: CSSProperties = { width: 60, height: 60, borderRadius: "50%", background: "#e3d6c2", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 18px" };
const btn: CSSProperties = { width: "100%", border: "none", borderRadius: 34, padding: "16px", marginTop: 12, cursor: "pointer", fontSize: 15, fontWeight: 600, background: "#000", color: "#fff" };
const btnGhost: CSSProperties = { background: "#fff", color: "#000", border: `1px solid ${LINE}` };
const textBtn: CSSProperties = {
  display: "block", width: "100%", textAlign: "center", background: "none", border: "none",
  fontSize: 14, fontWeight: 500, color: INK, padding: "14px", marginTop: 6, cursor: "pointer",
};
